// ===== Business Document Body Form (BD-C1 Phase 3: 완성도 기반 본문 입력) =====
// 카드 패널(.bdc-panel) 안에 인라인으로 펼쳐지는 본문 입력 폼이다. 모달이 아닌 이유는 카드가
// 대화 맥락 안에 있어야 하기 때문이다 — 방을 덮는 모달은 "이 메시지의 문서" 라는 연결을 끊는다.
//
// classic script 전역이다. import/export 를 한 줄도 두지 않는다 — tsconfig.frontend.json 이
// module: "None" 이라 모듈 키워드가 하나라도 있으면 이 파일이 TS 모듈이 되고, 그 순간
// business-document-card.ts 의 BusinessDocumentBodyForm 참조가 TS2304 로 typecheck 를 깨뜨린다.
// 선례: business-document-card.ts, work-inline-cards.ts (둘 다 import/export 0줄).
//
// renderMarkdown 을 경유하지 않는다(business-document-card.ts:11-13 과 같은 이유) — DOMPurify
// SANITIZE_CONFIG 가 data-* 를 스트립하므로 data-bdc-path 류 속성이 소실된다. 속성은 전부
// setAttribute 로 직접 붙이고 값은 textContent/value 로만 넣는다.
//
// ───────────────────────────────────────────────────────────────────────────
// 이 폼이 서버 계약에 맞추기 위해 지키는 네 가지 (전부 lib/ 실측에서 나왔다)
//
// 1. 파생값은 **표시만** 하고 patch 에 싣지 않는다.
//    applyDerivedBodyValues(body-derive.ts:269)가 저장 직전 items[] 각 요소에 amount 를 써
//    넣으므로, 서버에서 받은 items 를 그대로 되돌려 보내면 그 배열에는 이미 amount 가 있고
//    assertNoDerivedValuesInPatch(body-derive.ts:127)가 400 을 낸다. 그래서 배열 직렬화는
//    stripDerivedFromRows 를 반드시 거친다.
// 2. 배열은 **통째 교체**다(body-merge.ts:119-126). 한 줄만 고쳐도 patch 에는 배열 전체가
//    실린다 — 한 행만 보내면 나머지 행이 사라진다. 행 삭제도 null 이 아니라 "그 행이 빠진
//    배열 전체" 다(null 은 items 키 자체를 지운다).
// 3. 파생 판정은 **분기가 있다**. resolveDerivedBodyPaths(body-derive.ts:110-121)는
//    taxTreatment === "inclusive" 인 견적서에서 totalAmount 를 파생 목록에서 뺀다 — 그 분기에서
//    총액은 입력값이고 body-completeness.ts:144-147 이 그것을 필수 입력으로 요구한다. 폼도 같은
//    분기를 갖는다(derivedPaths). 이 분기를 프런트가 두 번째 진실로 갖지 않도록, 검증 게이트가
//    서버 판정자를 두 taxTreatment 값으로 호출해 **집합으로** 대조한다.
// 4. 문서번호(documentNumber/contractNumber)와 합계·부가세 같은 스칼라 파생값은 서술자 표에
//    아예 없다 — 입력 필드가 될 자리가 없다. 게이트가 서술자 키 집합 ∩ (항상-파생 ∪ 채번 키)
//    = 공집합을 단언한다.
// ───────────────────────────────────────────────────────────────────────────

type BusinessDocumentBodyFieldKind = "text" | "textarea" | "date" | "number" | "enum" | "array";

interface BusinessDocumentBodyDescriptor {
  kind: BusinessDocumentBodyFieldKind;
  label: string;
  // enum 전용. 값은 서버 zod 스키마에서 뜬 사본이고, 검증 게이트가
  // BUSINESS_DOCUMENT_BODY_SCHEMAS.<type>.shape.<key>.unwrap().options 와 값으로 대조한다.
  options?: string[];
  // enum 인데 저장 타입이 숫자인 것(vatRate = z.literal(0.1)). 표시는 문자열, 전송은 숫자다.
  numeric?: boolean;
  // 숫자 입력을 국내 표기 형식으로 자동 마스킹(사업자번호 3-2-5 / 전화번호). 서버 스키마가
  // 하이픈 포함 형식을 기대하는 칸에서 형식 위반 400 을 입력 시점에 예방한다.
  format?: "brn" | "phone";
  // 배열 전용. 요소가 객체인지(items·paymentMilestones·clauses) 문자열인지(attachments).
  element?: "object" | "string";
  // 조건부 필수. body-completeness.ts 의 FieldRule.when 과 **같은 술어**여야 한다 —
  // 갈라지면 폼이 서버가 요구하는 칸을 안 그리거나 서버가 거부할 칸을 그린다.
  when?: (body: Record<string, unknown>) => boolean;
  // 형식이 스키마 regex 로 고정된 칸의 예시 형식. placeholder 와 invalid_body 오류 문구에
  // 실린다 — 서버가 형식 위반을 400 으로 돌려줄 때 사용자가 고칠 단서가 이것뿐이다.
  hint?: string;
}

interface BusinessDocumentBodyObjectRow {
  // 서버에서 받은 원본 행. 서술자에 없는 키(specification·unit·remarks 등)를 보존한다 —
  // 배열이 통째 교체되므로 여기서 떨어뜨리면 그 값이 저장소에서 사라진다.
  base: Record<string, unknown>;
  // 사용자가 입력한 문자열. 저장 시점에 서술자 kind 로 강제 변환한다.
  edits: Record<string, string>;
}

interface BusinessDocumentBodyArrayDraft {
  element: "object" | "string";
  objectRows: BusinessDocumentBodyObjectRow[];
  stringRows: string[];
}

interface BusinessDocumentBodyFormOptions {
  panel: HTMLElement;
  documentId: string;
  documentType: string;
  body: unknown;
  // missingCount: 저장 응답의 completeness 에서 센 미입력 경로 수. 카드가 카운터·액센트를
  // 추가 왕복 없이 갱신하는 근거다(P3·P4). 미상일 수 있어 optional 이다.
  onSaved: (doc: Record<string, unknown>, missingCount?: number | null) => void;
  onClosed: (missingCount?: number | null) => void;
  // 폼 하단 [승인 요청]. 폼이 저장을 끝내고 스스로 닫힌 뒤 부른다 — 전이 실행은 카드 소유다.
  onSubmitRequest?: () => void;
  errorCopy: (code: string) => string;
}

interface BusinessDocumentBodyFormState {
  panel: HTMLElement;
  documentId: string;
  documentType: string;
  initialBody: Record<string, unknown>;
  scalars: Record<string, string>;
  arrays: Record<string, BusinessDocumentBodyArrayDraft>;
  missing: string[];
  // 서술자가 없는 스칼라 missing 경로. 문자열로만 실어 보낸다.
  unknown: string[];
  loaded: boolean;
  saving: boolean;
  // 마지막 저장 응답이 알려준 미입력 수. 닫기(취소) 시 카드에 넘겨 카운터가 낡지 않게 한다(P4).
  // null = 이 폼에서 아직 저장이 없었다.
  lastSavedMissingCount: number | null;
  onSaved: (doc: Record<string, unknown>, missingCount?: number | null) => void;
  onClosed: (missingCount?: number | null) => void;
  onSubmitRequest: () => void;
  errorCopy: (code: string) => string;
  root: HTMLElement | null;
  fields: HTMLElement | null;
  error: HTMLElement | null;
  savedNote: HTMLElement | null;
  // 입력 경로(data-bdc-path 와 같은 값) -> 라벨 래퍼. 서버 invalid_body 의 fields 를 칸
  // 하이라이트로 되돌리는 좌표계다. DOM 질의로 찾지 않는 이유: 테스트 하네스의 mock
  // querySelector 가 클래스 셀렉터만 지원하고(속성 셀렉터는 throw), 렌더 시점에 이미 손에
  // 있는 참조를 버렸다가 다시 찾는 것 자체가 낭비다. _renderFields 가 렌더마다 새로 채운다.
  fieldWraps: Record<string, HTMLElement>;
}

interface BusinessDocumentBodyFormResult {
  ok: boolean;
  code: string;
  payload: Record<string, unknown>;
}

var BusinessDocumentBodyForm = (function () {
  var {
    _LABEL_SAVE,
    _LABEL_CANCEL,
    _LABEL_SUBMIT,
  } = BusinessDocumentBodyFormParts.descriptors;
  var {
    _isRecord,
    _str,
    parsePath,
    derivedPaths,
    stripDerivedFromRows,
    requiredPaths,
    enumOptions,
    _descriptorPaths,
    _seedDraft,
    elementFieldPaths,
    buildPatch,
  } = BusinessDocumentBodyFormParts.helpers!;
  var {
    _el,
    _stopClicks,
    formatDerivedValue,
    _renderFields,
  } = BusinessDocumentBodyFormParts.fields!;
  var {
    applyPatchResponse,
    applyCompletenessResponse,
    _loadCompleteness,
    _save,
    _requestApproval,
    close,
  } = BusinessDocumentBodyFormParts.persistence!;

  function _mount(state: BusinessDocumentBodyFormState): void {
    var root = _el("div", "bdc-form");
    _stopClicks(root);

    // 저장 성공 안내(P4)는 폼 머리에 둔다 — 저장 버튼을 누른 시선이 폼 위쪽의 갱신된 미입력
    // 목록으로 돌아올 때 가장 먼저 읽히는 자리다.
    var savedNote = _el("div", "bdc-form-saved");
    savedNote.setAttribute("data-bdc-visible", "false");
    root.appendChild(savedNote);

    var fields = _el("div", "bdc-form-fields");
    root.appendChild(fields);

    var error = _el("div", "bdc-form-error");
    error.setAttribute("data-bdc-visible", "false");
    root.appendChild(error);

    // 하단 우측 버튼 3개(사용자 결정 2026-08-30): [닫기] [저장] [승인 요청].
    // 전진(승인 요청)이 오른쪽 끝 — 카드 스트립과 같은 문법이다. 정렬은 CSS(justify-content).
    var actions = _el("div", "bdc-form-actions");

    var cancel = _el("button", "ui-btn bdc-form-btn bdc-form-cancel") as HTMLButtonElement;
    cancel.type = "button";
    cancel.textContent = _LABEL_CANCEL;
    cancel.setAttribute("data-bdc-form-action", "cancel");
    cancel.addEventListener("click", function (event: Event) {
      event.stopPropagation();
      close(state);
      // 이 폼에서 저장이 있었으면 마지막으로 안 완성도를 카드에 넘긴다 — 닫은 뒤 카드 카운터가
      // 저장 전의 수를 주장하지 않게 한다(P4).
      state.onClosed(state.lastSavedMissingCount);
    });
    actions.appendChild(cancel);

    var save = _el("button", "ui-btn bdc-form-btn bdc-form-save") as HTMLButtonElement;
    save.type = "button";
    save.textContent = _LABEL_SAVE;
    save.setAttribute("data-bdc-form-action", "save");
    save.addEventListener("click", function (event: Event) {
      event.stopPropagation();
      void _save(state);
    });
    actions.appendChild(save);

    var submit = _el("button", "ui-btn bdc-form-btn bdc-form-submit") as HTMLButtonElement;
    submit.type = "button";
    submit.textContent = _LABEL_SUBMIT;
    submit.setAttribute("data-bdc-form-action", "submit");
    submit.addEventListener("click", function (event: Event) {
      event.stopPropagation();
      void _requestApproval(state);
    });
    actions.appendChild(submit);

    root.appendChild(actions);

    state.panel.appendChild(root);
    state.root = root;
    state.fields = fields;
    state.error = error;
    state.savedNote = savedNote;
    _renderFields(state);
  }

  /**
   * 카드의 [수정] 이 부른다. 이미 열려 있으면 먼저 걷어낸다 — 폼이 둘이면 같은 문서에 대해 서로
   * 다른 초안을 들고 서로를 덮어쓴다.
   */
  function open(options: BusinessDocumentBodyFormOptions): BusinessDocumentBodyFormState {
    var panel = options.panel;
    var existing = panel.querySelector(".bdc-form");
    if (existing && existing.parentNode) existing.parentNode.removeChild(existing);

    var state: BusinessDocumentBodyFormState = {
      panel: panel,
      documentId: options.documentId,
      documentType: _str(options.documentType),
      initialBody: _isRecord(options.body) ? options.body : {},
      scalars: {},
      arrays: {},
      missing: [],
      unknown: [],
      loaded: false,
      saving: false,
      lastSavedMissingCount: null,
      onSaved: typeof options.onSaved === "function" ? options.onSaved : function () {},
      onClosed: typeof options.onClosed === "function" ? options.onClosed : function () {},
      onSubmitRequest: typeof options.onSubmitRequest === "function" ? options.onSubmitRequest : function () {},
      errorCopy: typeof options.errorCopy === "function" ? options.errorCopy : function (code) { return code; },
      root: null,
      fields: null,
      error: null,
      savedNote: null,
      fieldWraps: {},
    };

    _seedDraft(state);
    _mount(state);
    void _loadCompleteness(state);
    return state;
  }

  return {
    open: open,
    close: close,
    // ── 게이트·테스트가 호출하는 순수 판정자들.
    //    문자열 스캔이 아니라 **함수 호출/집합 연산**으로 서버 상수와 대조하기 위해 노출한다.
    derivedPaths: derivedPaths,
    descriptorPaths: _descriptorPaths,
    requiredPaths: requiredPaths,
    enumOptions: enumOptions,
    formatDerivedValue: formatDerivedValue,
    elementFieldPaths: elementFieldPaths,
    stripDerivedFromRows: stripDerivedFromRows,
    parsePath: parsePath,
    buildPatch: buildPatch,
    applyPatchResponse: applyPatchResponse,
    applyCompletenessResponse: applyCompletenessResponse,
  };
})();
